import { Vector2 } from '../math/Vector2';

/** What happened to a pointer, in the vocabulary the engine cares about. */
export const PointerPhase = {
  /** A finger, pen or button went down. */
  Down: 'down',
  /** The pointer moved without changing its pressed state. */
  Move: 'move',
  /** The pointer was released normally. */
  Up: 'up',
  /** The browser took the pointer away, such as when a gesture was recognized. */
  Cancel: 'cancel',
  /** The pointer left the canvas. */
  Leave: 'leave',
} as const;

/** One of the {@link PointerPhase} values. */
export type PointerPhase = (typeof PointerPhase)[keyof typeof PointerPhase];

/**
 * One raw pointer event that knows where it happened.
 *
 * Coordinates are CSS pixels from the canvas' top left corner, matching the space
 * {@link Viewport} reports in, so whoever produced this has already subtracted the canvas' position on
 * the page and nothing downstream needs a device pixel ratio.
 */
export interface AbsolutePointerSample {
  /** Browser's id for this finger, pen or mouse. */
  readonly pointerId: number;
  /** What happened. */
  readonly phase: PointerPhase;
  /** CSS pixels from the canvas' left edge. */
  readonly x: number;
  /** CSS pixels from the canvas' top edge. */
  readonly y: number;
}

/**
 * One raw pointer event that knows only how far the pointer moved.
 *
 * What a locked pointer reports: the browser stops maintaining a cursor position and sends movement
 * instead, so the position becomes {@link InputManager}'s to keep. The absence of `x` and `y` is the
 * point of the separate shape. A locked `pointerdown` still arrives carrying the stale coordinates the
 * cursor was frozen at, and a sample that has nowhere to put them cannot pass them on.
 */
export interface RelativePointerSample {
  /** Browser's id for this finger, pen or mouse. */
  readonly pointerId: number;
  /** What happened. */
  readonly phase: PointerPhase;
  /** CSS pixels moved since the previous event, before sensitivity is applied. */
  readonly dx: number;
  /** CSS pixels moved since the previous event, before sensitivity is applied. */
  readonly dy: number;
}

/** One raw pointer event, already reduced to what the engine uses. */
export type PointerSample = AbsolutePointerSample | RelativePointerSample;

/** Whether a sample carries movement rather than a position. */
export function isRelativePointerSample(sample: PointerSample): sample is RelativePointerSample {
  return 'dx' in sample;
}

/** Whether a key went down or came up. */
export const KeyPhase = {
  /** The key was pressed. */
  Down: 'down',
  /** The key was released. */
  Up: 'up',
} as const;

/** One of the {@link KeyPhase} values. */
export type KeyPhase = (typeof KeyPhase)[keyof typeof KeyPhase];

/** One raw keyboard event. `code` is the physical key, so it does not shift with the layout. */
export interface KeySample {
  /** `KeyboardEvent.code`, such as `Space` or `ArrowLeft`. */
  readonly code: string;
  /** What happened. */
  readonly phase: KeyPhase;
}

/**
 * Where the pointer is and what it is doing, as of the current frame.
 *
 * Plain data, read by behaviors and by the interaction system. The `ThisFrame` flags are edges rather
 * than states: they are true for exactly one frame, which is what makes "did the player just tap" a
 * question a behavior can ask without remembering last frame itself.
 *
 * The press and release positions are recorded separately from the current one, because several events
 * can land between two frames: a tap that comes in as down, move and up together would otherwise be
 * hit-tested wherever the pointer finished rather than where the finger actually landed.
 *
 * Treat every field as read only. It is handed out directly rather than copied because it is read by
 * something on nearly every entity, every frame.
 */
export class PointerState {
  /**
   * CSS pixels from the canvas' top left corner.
   *
   * Where the pointer is as far as the game is concerned, which is not always where the operating
   * system's cursor is: under a pointer lock there is no cursor and this position is accumulated from
   * movement instead. Nothing above the input layer can tell the difference, which is the point.
   */
  readonly screenPosition = Vector2.zero();
  /** The same point in world units, through the active scene's camera. */
  readonly worldPosition = Vector2.zero();
  /** Where the most recent press landed, in CSS pixels. */
  readonly pressPosition = Vector2.zero();
  /** Where the most recent press landed, in world units. */
  readonly pressWorldPosition = Vector2.zero();
  /** Where the most recent release landed, in CSS pixels. */
  readonly releasePosition = Vector2.zero();
  /** Where the most recent release landed, in world units. */
  readonly releaseWorldPosition = Vector2.zero();
  /** Whether anything is currently pressed. Stays true while any of several fingers is down. */
  isDown = false;
  /** True for the one frame in which the first press landed. */
  pressedThisFrame = false;
  /** True for the one frame in which the last press was released. */
  releasedThisFrame = false;
  /** Whether the pointer is over the canvas at all. */
  isOverCanvas = false;
}
